import { GeoLocationContext } from "./GeoLocationContext"
import { useUserLocation, useDeviceLocation } from "hooks"
import config from "config"
import { useLocationHistory } from "./hooks"
import { useCallback, useMemo } from "react"
import { useVenue } from "providers/venue"
import _ from "lodash"
import { prepareGeolocationFeature } from "./utils/prepareGeolocationFeature.ts"

const userLocationStaleTime = config("userLocationStaleTime")
const isKioskMode = ["kiosk-v", "kiosk-h"].includes(config("device"))

export const GeoLocationProvider = ({ children }) => {
  const { levels, venues, dataLoaded, defaultVenue } = useVenue()
  const { deviceLocation } = useDeviceLocation()
  const { scannedLocations: scannedLocationsState, addScannedLocationHistory } =
    useLocationHistory()
  const { isLoading, userLocation: userLocationState } = useUserLocation(
    deviceLocation,
    {
      onUpdate: addScannedLocationHistory,
      staleTime: userLocationStaleTime,
      update: !isKioskMode,
      enable: dataLoaded,
    }
  )

  const userLocation = useMemo(
    () => prepareGeolocationFeature(userLocationState, { venues, levels }),
    [userLocationState, venues, levels]
  )

  const scannedLocations = useMemo(
    () =>
      scannedLocationsState.map((location) =>
        prepareGeolocationFeature(location, { venues, levels })
      ),
    [scannedLocationsState, levels, venues]
  )

  const findGeoLocationFeatureById = useCallback(
    (id) =>
      _.compact([userLocation, ...scannedLocations]).find(
        ({ id: geoLocationId }) => geoLocationId === id
      ),
    [userLocation, scannedLocations]
  )

  const locationVenue = useMemo(() => {
    return _.get(userLocation, "properties.venue", defaultVenue)
  }, [userLocation, defaultVenue])

  const value = {
    isLoading,
    userLocation,
    scannedLocations,
    deviceLocation,
    locationVenue,
    // Function
    findGeoLocationFeatureById,
  }

  return (
    <GeoLocationContext.Provider value={value}>
      {children}
    </GeoLocationContext.Provider>
  )
}
